import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Navigation
      dashboard: 'Dashboard',
      employees: 'Employees',
      attendance: 'Attendance',
      leave: 'Leave',
      timesheet: 'Timesheet',
      reports: 'Reports',
      
      // Common Actions
      add: 'Add',
      edit: 'Edit',
      delete: 'Delete',
      save: 'Save',
      cancel: 'Cancel',
      submit: 'Submit',
      close: 'Close',
      search: 'Search',
      filter: 'Filter',
      export: 'Export',
      import: 'Import',
      
      // Dashboard
      welcome: 'Welcome, {{name}}',
      totalEmployees: 'Total Employees',
      onLeave: 'On Leave',
      presentToday: 'Present Today',
      pendingRequests: 'Pending Requests',
      quickActions: 'Quick Actions',
      recentActivities: 'Recent Activities',
      
      // Employees
      addEmployee: 'Add Employee',
      employeeList: 'Employee List',
      employeeName: 'Employee Name',
      email: 'Email',
      position: 'Position',
      department: 'Department',
      status: 'Status',
      active: 'Active',
      inactive: 'Inactive',
      
      // Attendance
      checkIn: 'Check In',
      checkOut: 'Check Out',
      manualEntry: 'Manual Entry',
      attendanceHistory: 'Attendance History',
      present: 'Present',
      absent: 'Absent',
      late: 'Late',
      halfDay: 'Half Day',
      date: 'Date',
      time: 'Time',
      location: 'Location',
      
      // Leave Management
      requestLeave: 'Request Leave',
      leaveRequests: 'Leave Requests',
      leaveType: 'Leave Type',
      startDate: 'Start Date',
      endDate: 'End Date',
      reason: 'Reason',
      vacation: 'Vacation',
      sick: 'Sick Leave',
      personal: 'Personal',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      
      // Timesheet
      logHours: 'Log Hours',
      project: 'Project',
      hours: 'Hours',
      description: 'Description',
      totalHours: 'Total Hours',
      
      // Reports
      generateReport: 'Generate Report',
      attendanceReport: 'Attendance Report',
      leaveReport: 'Leave Report',
      timesheetReport: 'Timesheet Report',
      exportToPDF: 'Export to PDF',
      exportToCSV: 'Export to CSV',
      
      // Form Validation
      fieldRequired: 'This field is required',
      invalidEmail: 'Invalid email address',
      invalidDate: 'Invalid date',
      endDateBeforeStart: 'End date must be after start date',
      minimumCharacters: 'Minimum {{count}} characters required',
      
      // Messages
      success: {
        employeeAdded: 'Employee added successfully',
        employeeUpdated: 'Employee updated successfully',
        employeeDeleted: 'Employee deleted successfully',
        leaveSubmitted: 'Leave request submitted successfully',
        timesheetSaved: 'Timesheet entry saved successfully',
        checkInSuccess: 'Checked in successfully',
        checkOutSuccess: 'Checked out successfully',
      },
      error: {
        generic: 'An error occurred. Please try again.',
        networkError: 'Network error. Please check your connection.',
        unauthorized: 'You are not authorized to perform this action.',
      },
      
      // Settings
      settings: 'Settings',
      language: 'Language',
      theme: 'Theme',
      notifications: 'Notifications',
      profile: 'Profile',
      logout: 'Logout',
    },
  },
  es: {
    translation: {
      // Navigation
      dashboard: 'Panel',
      employees: 'Empleados',
      attendance: 'Asistencia',
      leave: 'Licencia',
      timesheet: 'Hoja de Horas',
      reports: 'Informes',
      
      // Common Actions
      add: 'Agregar',
      edit: 'Editar',
      delete: 'Eliminar',
      save: 'Guardar',
      cancel: 'Cancelar',
      submit: 'Enviar',
      close: 'Cerrar',
      search: 'Buscar',
      filter: 'Filtrar',
      export: 'Exportar',
      import: 'Importar',
      
      // Dashboard
      welcome: 'Bienvenido, {{name}}',
      totalEmployees: 'Total de Empleados',
      onLeave: 'De Licencia',
      presentToday: 'Presente Hoy',
      pendingRequests: 'Solicitudes Pendientes',
      quickActions: 'Acciones Rápidas',
      recentActivities: 'Actividades Recientes',
      
      // Employees
      addEmployee: 'Agregar Empleado',
      employeeList: 'Lista de Empleados',
      employeeName: 'Nombre del Empleado',
      email: 'Correo Electrónico',
      position: 'Posición',
      department: 'Departamento',
      status: 'Estado',
      active: 'Activo',
      inactive: 'Inactivo',
      
      // Attendance
      checkIn: 'Registrar Entrada',
      checkOut: 'Registrar Salida',
      manualEntry: 'Entrada Manual',
      attendanceHistory: 'Historial de Asistencia',
      present: 'Presente',
      absent: 'Ausente',
      late: 'Tarde',
      halfDay: 'Medio Día',
      date: 'Fecha',
      time: 'Hora',
      location: 'Ubicación',
      
      // Leave Management
      requestLeave: 'Solicitar Licencia',
      leaveRequests: 'Solicitudes de Licencia',
      leaveType: 'Tipo de Licencia',
      startDate: 'Fecha de Inicio',
      endDate: 'Fecha de Fin',
      reason: 'Razón',
      vacation: 'Vacaciones',
      sick: 'Licencia por Enfermedad',
      personal: 'Personal',
      pending: 'Pendiente',
      approved: 'Aprobado',
      rejected: 'Rechazado',
      
      // Timesheet
      logHours: 'Registrar Horas',
      project: 'Proyecto',
      hours: 'Horas',
      description: 'Descripción',
      totalHours: 'Total de Horas',
      
      // Reports
      generateReport: 'Generar Informe',
      attendanceReport: 'Informe de Asistencia',
      leaveReport: 'Informe de Licencias',
      timesheetReport: 'Informe de Horas',
      exportToPDF: 'Exportar a PDF',
      exportToCSV: 'Exportar a CSV',
      
      // Form Validation
      fieldRequired: 'Este campo es obligatorio',
      invalidEmail: 'Dirección de correo electrónico inválida',
      invalidDate: 'Fecha inválida',
      endDateBeforeStart: 'La fecha de fin debe ser posterior a la fecha de inicio',
      minimumCharacters: 'Se requieren mínimo {{count}} caracteres',
      
      // Messages
      success: {
        employeeAdded: 'Empleado agregado exitosamente',
        employeeUpdated: 'Empleado actualizado exitosamente',
        employeeDeleted: 'Empleado eliminado exitosamente',
        leaveSubmitted: 'Solicitud de licencia enviada exitosamente',
        timesheetSaved: 'Entrada de hoja de horas guardada exitosamente',
        checkInSuccess: 'Entrada registrada exitosamente',
        checkOutSuccess: 'Salida registrada exitosamente',
      },
      error: {
        generic: 'Ocurrió un error. Por favor, inténtelo de nuevo.',
        networkError: 'Error de red. Por favor, verifique su conexión.',
        unauthorized: 'No está autorizado para realizar esta acción.',
      },
      
      // Settings
      settings: 'Configuración',
      language: 'Idioma',
      theme: 'Tema',
      notifications: 'Notificaciones',
      profile: 'Perfil',
      logout: 'Cerrar Sesión',
    },
  },
  fr: {
    translation: {
      // Navigation
      dashboard: 'Tableau de Bord',
      employees: 'Employés',
      attendance: 'Présence',
      leave: 'Congé',
      timesheet: 'Feuille de Temps',
      reports: 'Rapports',
      
      // Common Actions
      add: 'Ajouter',
      edit: 'Modifier',
      delete: 'Supprimer',
      save: 'Enregistrer',
      cancel: 'Annuler',
      submit: 'Soumettre',
      close: 'Fermer',
      search: 'Rechercher',
      filter: 'Filtrer',
      export: 'Exporter',
      import: 'Importer',
      
      // Dashboard
      welcome: 'Bienvenue, {{name}}',
      totalEmployees: 'Total des Employés',
      onLeave: 'En Congé',
      presentToday: 'Présent Aujourd\'hui',
      pendingRequests: 'Demandes en Attente',
      quickActions: 'Actions Rapides',
      recentActivities: 'Activités Récentes',
      
      // Employees
      addEmployee: 'Ajouter un Employé',
      employeeList: 'Liste des Employés',
      employeeName: 'Nom de l\'Employé',
      email: 'E-mail',
      position: 'Poste',
      department: 'Département',
      status: 'Statut',
      active: 'Actif',
      inactive: 'Inactif',
      
      // Attendance
      checkIn: 'Pointer l\'Arrivée',
      checkOut: 'Pointer le Départ',
      manualEntry: 'Saisie Manuelle',
      attendanceHistory: 'Historique de Présence',
      present: 'Présent',
      absent: 'Absent',
      late: 'En Retard',
      halfDay: 'Demi-Journée',
      date: 'Date',
      time: 'Heure',
      location: 'Emplacement',
      
      // Leave Management
      requestLeave: 'Demander un Congé',
      leaveRequests: 'Demandes de Congé',
      leaveType: 'Type de Congé',
      startDate: 'Date de Début',
      endDate: 'Date de Fin',
      reason: 'Raison',
      vacation: 'Vacances',
      sick: 'Congé Maladie',
      personal: 'Personnel',
      pending: 'En Attente',
      approved: 'Approuvé',
      rejected: 'Rejeté',
      
      // Timesheet
      logHours: 'Enregistrer les Heures',
      project: 'Projet',
      hours: 'Heures',
      description: 'Description',
      totalHours: 'Total des Heures',
      
      // Reports
      generateReport: 'Générer un Rapport',
      attendanceReport: 'Rapport de Présence',
      leaveReport: 'Rapport de Congés',
      timesheetReport: 'Rapport des Heures',
      exportToPDF: 'Exporter en PDF',
      exportToCSV: 'Exporter en CSV',
      
      // Form Validation
      fieldRequired: 'Ce champ est obligatoire',
      invalidEmail: 'Adresse e-mail invalide',
      invalidDate: 'Date invalide',
      endDateBeforeStart: 'La date de fin doit être postérieure à la date de début',
      minimumCharacters: 'Minimum {{count}} caractères requis',
      
      // Messages
      success: {
        employeeAdded: 'Employé ajouté avec succès',
        employeeUpdated: 'Employé mis à jour avec succès',
        employeeDeleted: 'Employé supprimé avec succès',
        leaveSubmitted: 'Demande de congé soumise avec succès',
        timesheetSaved: 'Entrée de feuille de temps enregistrée avec succès',
        checkInSuccess: 'Arrivée pointée avec succès',
        checkOutSuccess: 'Départ pointé avec succès',
      },
      error: {
        generic: 'Une erreur s\'est produite. Veuillez réessayer.',
        networkError: 'Erreur réseau. Veuillez vérifier votre connexion.',
        unauthorized: 'Vous n\'êtes pas autorisé à effectuer cette action.',
      },
      
      // Settings
      settings: 'Paramètres',
      language: 'Langue',
      theme: 'Thème',
      notifications: 'Notifications',
      profile: 'Profil',
      logout: 'Déconnexion',
    },
  },
  de: {
    translation: {
      dashboard: 'Instrumententafel', employees: 'Mitarbeiter', attendance: 'Anwesenheit', leave: 'Urlaub', timesheet: 'Stundenzettel', reports: 'Berichte', add: 'Hinzufügen', edit: 'Bearbeiten', delete: 'Löschen', save: 'Speichern', cancel: 'Abbrechen', submit: 'Einreichen', close: 'Schließen', search: 'Suchen', filter: 'Filtern', export: 'Exportieren', import: 'Importieren', welcome: 'Willkommen, {{name}}', totalEmployees: 'Gesamtzahl Mitarbeiter', onLeave: 'Im Urlaub', presentToday: 'Heute Anwesend', pendingRequests: 'Ausstehende Anfragen', quickActions: 'Schnellaktionen', recentActivities: 'Letzte Aktivitäten', addEmployee: 'Mitarbeiter Hinzufügen', employeeList: 'Mitarbeiterliste', employeeName: 'Name des Mitarbeiters', email: 'E-Mail', position: 'Position', department: 'Abteilung', status: 'Status', active: 'Aktiv', inactive: 'Inaktiv', checkIn: 'Einchecken', checkOut: 'Auschecken', manualEntry: 'Manuelle Eingabe', attendanceHistory: 'Anwesenheitsverlauf', present: 'Anwesend', absent: 'Abwesend', late: 'Zu Spät', halfDay: 'Halber Tag', date: 'Datum', time: 'Zeit', location: 'Ort', requestLeave: 'Urlaub Anfordern', leaveRequests: 'Urlaubsanfragen', leaveType: 'Urlaubstyp', startDate: 'Startdatum', endDate: 'Enddatum', reason: 'Grund', vacation: 'Urlaub', sick: 'Krankheitsurlaub', personal: 'Persönlich', pending: 'Ausstehend', approved: 'Genehmigt', rejected: 'Abgelehnt', logHours: 'Stunden Protokollieren', project: 'Projekt', hours: 'Stunden', description: 'Beschreibung', totalHours: 'Gesamtstunden', generateReport: 'Bericht Generieren', attendanceReport: 'Anwesenheitsbericht', leaveReport: 'Urlaubsbericht', timesheetReport: 'Stundenzettelbericht', exportToPDF: 'Als PDF Exportieren', exportToCSV: 'Als CSV Exportieren', fieldRequired: 'Dieses Feld ist erforderlich', invalidEmail: 'Ungültige E-Mail-Adresse', invalidDate: 'Ungültiges Datum', endDateBeforeStart: 'Enddatum muss nach Startdatum liegen', minimumCharacters: 'Mindestens {{count}} Zeichen erforderlich', success: { employeeAdded: 'Mitarbeiter erfolgreich hinzugefügt', employeeUpdated: 'Mitarbeiter erfolgreich aktualisiert', employeeDeleted: 'Mitarbeiter erfolgreich gelöscht', leaveSubmitted: 'Urlaubsantrag erfolgreich eingereicht', timesheetSaved: 'Stundenzetteleintrag erfolgreich gespeichert', checkInSuccess: 'Erfolgreich eingecheckt', checkOutSuccess: 'Erfolgreich ausgecheckt', }, error: { generic: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.', networkError: 'Netzwerkfehler. Bitte überprüfen Sie Ihre Verbindung.', unauthorized: 'Sie sind nicht berechtigt, diese Aktion durchzuführen.', }, settings: 'Einstellungen', language: 'Sprache', theme: 'Design', notifications: 'Benachrichtigungen', profile: 'Profil', logout: 'Abmelden',
    },
  },
  pt: {
    translation: {
      dashboard: 'Painel de Controle', employees: 'Funcionários', attendance: 'Presença', leave: 'Licença', timesheet: 'Folha de Ponto', reports: 'Relatórios', add: 'Adicionar', edit: 'Editar', delete: 'Excluir', save: 'Salvar', cancel: 'Cancelar', submit: 'Enviar', close: 'Fechar', search: 'Pesquisar', filter: 'Filtrar', export: 'Exportar', import: 'Importar', welcome: 'Bem-vindo, {{name}}', totalEmployees: 'Total de Funcionários', onLeave: 'De Licença', presentToday: 'Presente Hoje', pendingRequests: 'Solicitações Pendentes', quickActions: 'Ações Rápidas', recentActivities: 'Atividades Recentes', addEmployee: 'Adicionar Funcionário', employeeList: 'Lista de Funcionários', employeeName: 'Nome do Funcionário', email: 'E-mail', position: 'Cargo', department: 'Departamento', status: 'Status', active: 'Ativo', inactive: 'Inativo', checkIn: 'Entrada', checkOut: 'Saída', manualEntry: 'Entrada Manual', attendanceHistory: 'Histórico de Presença', present: 'Presente', absent: 'Ausente', late: 'Atrasado', halfDay: 'Meio Dia', date: 'Data', time: 'Hora', location: 'Local', requestLeave: 'Solicitar Licença', leaveRequests: 'Solicitações de Licença', leaveType: 'Tipo de Licença', startDate: 'Data de Início', endDate: 'Data de Término', reason: 'Motivo', vacation: 'Férias', sick: 'Licença Médica', personal: 'Pessoal', pending: 'Pendente', approved: 'Aprovado', rejected: 'Rejeitado', logHours: 'Registrar Horas', project: 'Projeto', hours: 'Horas', description: 'Descrição', totalHours: 'Total de Horas', generateReport: 'Gerar Relatório', attendanceReport: 'Relatório de Presença', leaveReport: 'Relatório de Licenças', timesheetReport: 'Relatório de Horas', exportToPDF: 'Exportar para PDF', exportToCSV: 'Exportar para CSV', fieldRequired: 'Este campo é obrigatório', invalidEmail: 'Endereço de e-mail inválido', invalidDate: 'Data inválida', endDateBeforeStart: 'A data de término deve ser posterior à data de início', minimumCharacters: 'Mínimo {{count}} caracteres necessários', success: { employeeAdded: 'Funcionário adicionado com sucesso', employeeUpdated: 'Funcionário atualizado com sucesso', employeeDeleted: 'Funcionário excluído com sucesso', leaveSubmitted: 'Solicitação de licença enviada com sucesso', timesheetSaved: 'Registro de folha de ponto salvo com sucesso', checkInSuccess: 'Entrada registrada com sucesso', checkOutSuccess: 'Saída registrada com sucesso', }, error: { generic: 'Ocorreu um erro. Por favor, tente novamente.', networkError: 'Erro de rede. Verifique sua conexão.', unauthorized: 'Você não está autorizado a executar esta ação.', }, settings: 'Configurações', language: 'Idioma', theme: 'Tema', notifications: 'Notificações', profile: 'Perfil', logout: 'Sair',
    },
  },
  hi: {
    translation: {
      dashboard: 'डैशबोर्ड', employees: 'कर्मचारी', attendance: 'उपस्थिति', leave: 'छुट्टी', timesheet: 'समय पत्र', reports: 'रिपोर्ट', add: 'जोड़ें', edit: 'संपादित करें', delete: 'हटाएं', save: 'सहेजें', cancel: 'रद्द करें', submit: 'जमा करें', close: 'बंद करें', search: 'खोजें', filter: 'फिल्टर', export: 'निर्यात करें', import: 'आयात करें', welcome: 'स्वागत है, {{name}}', totalEmployees: 'कुल कर्मचारी', onLeave: 'छुट्टी पर', presentToday: 'आज मौजूद', pendingRequests: 'लंबित अनुरोध', quickActions: 'त्वरित कार्य', recentActivities: 'हाल की गतिविधियां', addEmployee: 'कर्मचारी जोड़ें', employeeList: 'कर्मचारी सूची', employeeName: 'कर्मचारी का नाम', email: 'ईमेल', position: 'पद', department: 'विभाग', status: 'स्थिति', active: 'सक्रिय', inactive: 'निष्क्रिय', checkIn: 'चेक इन करें', checkOut: 'चेक आउट करें', manualEntry: 'मैनुअल प्रविष्टि', attendanceHistory: 'उपस्थिति इतिहास', present: 'उपस्थित', absent: 'अनुपस्थित', late: 'देर से', halfDay: 'आधा दिन', date: 'तारीख', time: 'समय', location: 'स्थान', requestLeave: 'छुट्टी का अनुरोध करें', leaveRequests: 'छुट्टी के अनुरोध', leaveType: 'छुट्टी का प्रकार', startDate: 'प्रारंभ तारीख', endDate: 'अंत तारीख', reason: 'कारण', vacation: 'छुट्टी', sick: 'बीमारी की छुट्टी', personal: 'व्यक्तिगत', pending: 'लंबित', approved: 'अनुमोदित', rejected: 'अस्वीकृत', logHours: 'घंटे दर्ज करें', project: 'परियोजना', hours: 'घंटे', description: 'विवरण', totalHours: 'कुल घंटे', generateReport: 'रिपोर्ट जनरेट करें', attendanceReport: 'उपस्थिति रिपोर्ट', leaveReport: 'छुट्टी रिपोर्ट', timesheetReport: 'समय पत्र रिपोर्ट', exportToPDF: 'PDF में निर्यात करें', exportToCSV: 'CSV में निर्यात करें', fieldRequired: 'यह फील्ड आवश्यक है', invalidEmail: 'अमान्य ईमेल पता', invalidDate: 'अमान्य तारीख', endDateBeforeStart: 'अंत तारीख प्रारंभ तारीख के बाद होनी चाहिए', minimumCharacters: 'न्यूनतम {{count}} वर्ण आवश्यक', success: { employeeAdded: 'कर्मचारी सफलतापूर्वक जोड़ा गया', employeeUpdated: 'कर्मचारी सफलतापूर्वक अपडेट किया गया', employeeDeleted: 'कर्मचारी सफलतापूर्वक हटाया गया', leaveSubmitted: 'छुट्टी का अनुरोध सफलतापूर्वक जमा किया गया', timesheetSaved: 'समय पत्र प्रविष्टि सफलतापूर्वक सहेजी गई', checkInSuccess: 'सफलतापूर्वक चेक इन किया गया', checkOutSuccess: 'सफलतापूर्वक चेक आउट किया गया', }, error: { generic: 'एक त्रुटि हुई। कृपया पुनः प्रयास करें।', networkError: 'नेटवर्क त्रुटि। अपना कनेक्शन जांचें।', unauthorized: 'आप इस कार्य को करने के लिए अधिकृत नहीं हैं।', }, settings: 'सेटिंग्स', language: 'भाषा', theme: 'थीम', notifications: 'सूचनाएं', profile: 'प्रोफाइल', logout: 'लॉगआउट',
    },
  },
  ar: {
    translation: {
      dashboard: 'لوحة التحكم', employees: 'الموظفون', attendance: 'الحضور', leave: 'إجازة', timesheet: 'ورقة الوقت', reports: 'التقارير', add: 'إضافة', edit: 'تعديل', delete: 'حذف', save: 'حفظ', cancel: 'إلغاء', submit: 'إرسال', close: 'إغلاق', search: 'بحث', filter: 'تصفية', export: 'تصدير', import: 'استيراد', welcome: 'مرحبا، {{name}}', totalEmployees: 'إجمالي الموظفين', onLeave: 'في إجازة', presentToday: 'حاضر اليوم', pendingRequests: 'الطلبات المعلقة', quickActions: 'الإجراءات السريعة', recentActivities: 'الأنشطة الأخيرة', addEmployee: 'إضافة موظف', employeeList: 'قائمة الموظفين', employeeName: 'اسم الموظف', email: 'البريد الإلكتروني', position: 'المنصب', department: 'القسم', status: 'الحالة', active: 'نشط', inactive: 'غير نشط', checkIn: 'تسجيل الدخول', checkOut: 'تسجيل الخروج', manualEntry: 'إدخال يدوي', attendanceHistory: 'سجل الحضور', present: 'حاضر', absent: 'غائب', late: 'متأخر', halfDay: 'نصف يوم', date: 'التاريخ', time: 'الوقت', location: 'الموقع', requestLeave: 'طلب إجازة', leaveRequests: 'طلبات الإجازة', leaveType: 'نوع الإجازة', startDate: 'تاريخ البداية', endDate: 'تاريخ النهاية', reason: 'السبب', vacation: 'عطلة', sick: 'إجازة مرضية', personal: 'شخصي', pending: 'معلق', approved: 'موافق عليه', rejected: 'مرفوض', logHours: 'تسجيل الساعات', project: 'المشروع', hours: 'الساعات', description: 'الوصف', totalHours: 'إجمالي الساعات', generateReport: 'إنشاء تقرير', attendanceReport: 'تقرير الحضور', leaveReport: 'تقرير الإجازات', timesheetReport: 'تقرير ورقة الوقت', exportToPDF: 'تصدير إلى PDF', exportToCSV: 'تصدير إلى CSV', fieldRequired: 'هذا الحقل مطلوب', invalidEmail: 'عنوان بريد إلكتروني غير صحيح', invalidDate: 'تاريخ غير صحيح', endDateBeforeStart: 'يجب أن يكون تاريخ النهاية بعد تاريخ البداية', minimumCharacters: 'الحد الأدنى {{count}} أحرف مطلوبة', success: { employeeAdded: 'تمت إضافة الموظف بنجاح', employeeUpdated: 'تم تحديث الموظف بنجاح', employeeDeleted: 'تم حذف الموظف بنجاح', leaveSubmitted: 'تم إرسال طلب الإجازة بنجاح', timesheetSaved: 'تم حفظ إدخال ورقة الوقت بنجاح', checkInSuccess: 'تم تسجيل الدخول بنجاح', checkOutSuccess: 'تم تسجيل الخروج بنجاح', }, error: { generic: 'حدث خطأ. يرجى المحاولة مرة أخرى.', networkError: 'خطأ في الشبكة. يرجى التحقق من اتصالك.', unauthorized: 'أنت غير مصرح بتنفيذ هذا الإجراء.', }, settings: 'الإعدادات', language: 'اللغة', theme: 'المظهر', notifications: 'الإخطارات', profile: 'الملف الشخصي', logout: 'تسجيل الخروج',
    },
  },
  zh: {
    translation: {
      dashboard: '仪表板', employees: '员工', attendance: '出勤', leave: '休假', timesheet: '时间表', reports: '报告', add: '添加', edit: '编辑', delete: '删除', save: '保存', cancel: '取消', submit: '提交', close: '关闭', search: '搜索', filter: '过滤', export: '导出', import: '导入', welcome: '欢迎，{{name}}', totalEmployees: '员工总数', onLeave: '休假中', presentToday: '今天到场', pendingRequests: '待审核请求', quickActions: '快速操作', recentActivities: '最近活动', addEmployee: '添加员工', employeeList: '员工列表', employeeName: '员工名字', email: '电子邮件', position: '职位', department: '部门', status: '状态', active: '活跃', inactive: '非活跃', checkIn: '签到', checkOut: '签出', manualEntry: '手动输入', attendanceHistory: '出勤历史', present: '到场', absent: '缺席', late: '迟到', halfDay: '半天', date: '日期', time: '时间', location: '位置', requestLeave: '申请休假', leaveRequests: '休假请求', leaveType: '休假类型', startDate: '开始日期', endDate: '结束日期', reason: '原因', vacation: '假期', sick: '病假', personal: '个人', pending: '待审核', approved: '已批准', rejected: '已拒绝', logHours: '记录小时', project: '项目', hours: '小时', description: '描述', totalHours: '总小时数', generateReport: '生成报告', attendanceReport: '出勤报告', leaveReport: '休假报告', timesheetReport: '时间表报告', exportToPDF: '导出为PDF', exportToCSV: '导出为CSV', fieldRequired: '此字段为必填项', invalidEmail: '无效的电子邮件地址', invalidDate: '无效的日期', endDateBeforeStart: '结束日期必须在开始日期之后', minimumCharacters: '至少需要 {{count}} 个字符', success: { employeeAdded: '员工添加成功', employeeUpdated: '员工更新成功', employeeDeleted: '员工删除成功', leaveSubmitted: '休假请求提交成功', timesheetSaved: '时间表条目保存成功', checkInSuccess: '签到成功', checkOutSuccess: '签出成功', }, error: { generic: '发生错误。请重试。', networkError: '网络错误。请检查您的连接。', unauthorized: '您无权执行此操作。', }, settings: '设置', language: '语言', theme: '主题', notifications: '通知', profile: '档案', logout: '登出',
    },
  },
  ja: {
    translation: {
      dashboard: 'ダッシュボード', employees: '従業員', attendance: '出勤', leave: '休暇', timesheet: 'タイムシート', reports: 'レポート', add: '追加', edit: '編集', delete: '削除', save: '保存', cancel: 'キャンセル', submit: '送信', close: '閉じる', search: '検索', filter: 'フィルタ', export: 'エクスポート', import: 'インポート', welcome: 'ようこそ、{{name}}', totalEmployees: '従業員総数', onLeave: '休暇中', presentToday: '本日出勤', pendingRequests: '保留中のリクエスト', quickActions: 'クイックアクション', recentActivities: '最近のアクティビティ', addEmployee: '従業員を追加', employeeList: '従業員リスト', employeeName: '従業員名', email: 'メール', position: '職位', department: '部門', status: 'ステータス', active: 'アクティブ', inactive: '非アクティブ', checkIn: 'チェックイン', checkOut: 'チェックアウト', manualEntry: '手動入力', attendanceHistory: '出勤履歴', present: '出勤', absent: '欠席', late: '遅刻', halfDay: '午前/午後休', date: '日付', time: '時間', location: '場所', requestLeave: '休暇をリクエスト', leaveRequests: '休暇リクエスト', leaveType: '休暇の種類', startDate: '開始日', endDate: '終了日', reason: '理由', vacation: '有給休暇', sick: '病気休暇', personal: '個人', pending: '保留中', approved: '承認済み', rejected: '却下', logHours: '時間を記録', project: 'プロジェクト', hours: '時間', description: '説明', totalHours: '合計時間', generateReport: 'レポートを生成', attendanceReport: '出勤レポート', leaveReport: '休暇レポート', timesheetReport: 'タイムシートレポート', exportToPDF: 'PDFとしてエクスポート', exportToCSV: 'CSVとしてエクスポート', fieldRequired: 'このフィールドは必須です', invalidEmail: '無効なメールアドレス', invalidDate: '無効な日付', endDateBeforeStart: '終了日は開始日より後である必要があります', minimumCharacters: '最低 {{count}} 文字が必要です', success: { employeeAdded: '従業員が正常に追加されました', employeeUpdated: '従業員が正常に更新されました', employeeDeleted: '従業員が正常に削除されました', leaveSubmitted: '休暇リクエストが正常に送信されました', timesheetSaved: 'タイムシートエントリが正常に保存されました', checkInSuccess: 'チェックインに成功しました', checkOutSuccess: 'チェックアウトに成功しました', }, error: { generic: 'エラーが発生しました。もう一度お試しください。', networkError: 'ネットワークエラー。接続を確認してください。', unauthorized: 'このアクションを実行する権限がありません。', }, settings: '設定', language: '言語', theme: 'テーマ', notifications: '通知', profile: 'プロフィール', logout: 'ログアウト',
    },
  },
  ko: {
    translation: {
      dashboard: '대시보드', employees: '직원', attendance: '출석', leave: '휴가', timesheet: '근무표', reports: '보고서', add: '추가', edit: '수정', delete: '삭제', save: '저장', cancel: '취소', submit: '제출', close: '닫기', search: '검색', filter: '필터', export: '내보내기', import: '가져오기', welcome: '환영합니다, {{name}}', totalEmployees: '총 직원 수', onLeave: '휴가 중', presentToday: '오늘 출석', pendingRequests: '보류 중인 요청', quickActions: '빠른 작업', recentActivities: '최근 활동', addEmployee: '직원 추가', employeeList: '직원 목록', employeeName: '직원 이름', email: '이메일', position: '직책', department: '부서', status: '상태', active: '활성', inactive: '비활성', checkIn: '체크인', checkOut: '체크아웃', manualEntry: '수동 입력', attendanceHistory: '출석 기록', present: '출석', absent: '결석', late: '지각', halfDay: '반일', date: '날짜', time: '시간', location: '위치', requestLeave: '휴가 신청', leaveRequests: '휴가 신청', leaveType: '휴가 유형', startDate: '시작 날짜', endDate: '종료 날짜', reason: '사유', vacation: '휴가', sick: '병가', personal: '개인', pending: '보류 중', approved: '승인됨', rejected: '거부됨', logHours: '시간 기록', project: '프로젝트', hours: '시간', description: '설명', totalHours: '총 시간', generateReport: '보고서 생성', attendanceReport: '출석 보고서', leaveReport: '휴가 보고서', timesheetReport: '근무표 보고서', exportToPDF: 'PDF로 내보내기', exportToCSV: 'CSV로 내보내기', fieldRequired: '이 필드는 필수입니다', invalidEmail: '잘못된 이메일 주소', invalidDate: '잘못된 날짜', endDateBeforeStart: '종료 날짜는 시작 날짜 이후여야 합니다', minimumCharacters: '최소 {{count}} 자 필요', success: { employeeAdded: '직원이 성공적으로 추가되었습니다', employeeUpdated: '직원이 성공적으로 업데이트되었습니다', employeeDeleted: '직원이 성공적으로 삭제되었습니다', leaveSubmitted: '휴가 신청이 성공적으로 제출되었습니다', timesheetSaved: '근무표 항목이 성공적으로 저장되었습니다', checkInSuccess: '성공적으로 체크인되었습니다', checkOutSuccess: '성공적으로 체크아웃되었습니다', }, error: { generic: '오류가 발생했습니다. 다시 시도하세요.', networkError: '네트워크 오류입니다. 연결을 확인하세요.', unauthorized: '이 작업을 수행할 권한이 없습니다.', }, settings: '설정', language: '언어', theme: '테마', notifications: '알림', profile: '프로필', logout: '로그아웃',
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

export default i18n;
